<template>
    <div class="flex flex-wrap gap-x-4 gap-y-2 items-center">
        <div v-for="(label, i) in items" :key="i" class="flex items-center gap-2">
            <span class="inline-block w-3 h-3 rounded-sm" :class="paletteColor(i, items.length)"></span>
            <span class="details text-ink-muted">{{ label }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { resolveScalePointLabels } from "~vue/utils/assessmentValidation";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { paletteColor } from "./distributionPalette";

const { t } = useI18n();

interface Props {
    maxScore: number;
    labels?: string[];
    scale?: string | null;
    // Each label's 1-based position in the scale's full, un-hidden point set
    // (`PerQuestion.scale_point_indices`) — absent on reports generated
    // before BRA-1259, in which case the saved labels render untranslated
    // rather than guessing a point from array position (see
    // resolveScalePointLabels: a wrong guess would relabel an already-
    // published result, even in English).
    pointIndices?: number[] | null;
}

const props = defineProps<Props>();

const items = computed<string[]>(() => {
    if (props.labels && props.labels.length === props.maxScore) {
        const translated = resolveScalePointLabels(props.scale, props.labels, props.pointIndices);
        // The displayed "N ·" number stays the label's position among the
        // ones actually shown (matching the distribution bar's own axis),
        // even when the underlying point index — used only to look up the
        // right translation — skips a hidden neutral midpoint.
        return translated.map((label, i) => t("assessments.results.distribution.legendItem", { score: formatNumber(i + 1), label }));
    }
    return Array.from({ length: props.maxScore }, (_, i) => formatNumber(i + 1));
});
</script>
